import {
  put, all, call, select, takeEvery, takeLatest, fork, cancel,
} from 'redux-saga/effects';
import { generatePath } from 'react-router';
import { getCurrentAuthHeader } from 'domain/fuse/auth/utils';
import * as I18n from 'circe/I18n';
import { exception } from 'circe/logger';
import { show as showNotification } from 'circe/notifications/actions';
import moment from 'moment';
import { getCurrentProfile } from 'domain/fuse/auth/selectors';
import { ROUTES as ORDER_ROUTES } from 'domain/shipment/order/constants';
import * as api from 'domain/shipment/newOrder/api';
import {
  create,
  created,
  creating,
  failed,
  fetchedFleets,
  fetchFleets,
} from 'domain/shipment/newOrder/actions';
import { ROUTES } from 'domain/shipment/constants';
import { getCoordinates } from 'domain/shipment/newOrder/utils';

function* doCreate(context, action) {
  const { payload } = action;

  const { data } = payload;
  let resp;

  try {
    const state = yield select();

    const headers = yield call(getCurrentAuthHeader, state);

    const currentProfile = getCurrentProfile(state);

    yield put(creating());
    let request = {
      fleetId: data.selectedFleet ? data.selectedFleet.account : null,
      maxLoadWeightPerShipment:
        data.splitBy !== 'other' ? parseFloat(data.splitBy) : parseFloat(data.weight),
      salesCategory: data.salesCategory,
      referenceId: data.referenceNumber,
      unit: 'MT',
      customerCode: data.customerCode,
      truckType: data.truckType,
      quantity: parseFloat(data.quantity),
      modeOfDelivery: data.modeOfDelivery,
      poExpiryDate: data.poExpiryDate
        ? moment(data.poExpiryDate)
          .add(3, 'hours')
          .toISOString()
        : undefined,
      pickup: {
        address: data.warehouseLoadingPoint.name,
        arrivalWindow: {
          begin: moment(data.shipDate)
            .add(3, 'hours')
            .toISOString(),
          end: moment(data.shipDate)
            .add(3, 'hours')
            .toISOString(),
          excludeBegin: true,
          excludeEnd: true,
        },
        coordinate: {
          latitude: data.warehouseLoadingPoint.location.lat,
          longitude: data.warehouseLoadingPoint.location.lng,
        },
        description: data.warehouseLoadingPoint.name,
        duration: null,
      },
      shipperId: data.shipperId ? data.shipperId : currentProfile.profileId,
      notificationContact: null,
      senderContact: null,
      receiverContact: null,
    };

    if (data.salesCategoryDivision) {
      request.vehicleTags = [`division:${data.salesCategoryDivision.toLowerCase()}`];
    }

    if (data.modeOfDelivery === 'DAT') {
      if (!data.forecast) {
        request = {
          ...request,
          salesOrderNumber: data.salesOrderNumber,
          salesOrderLineNumber: parseFloat(data.salesOrderLineNumber),
          itemNumber: data.itemNumber,
          dropoff: {
            address: data.toAddress[0].formatted_address,
            arrivalWindow: {
              begin: moment(data.deliveryDate)
                .add(3, 'hours')
                .toISOString(),
              end: moment(data.deliveryDate)
                .add(3, 'hours')
                .toISOString(),
              excludeBegin: true,
              excludeEnd: true,
            },
            coordinate: getCoordinates(data.toAddress[0]),
            description: data.toAddress[0].formatted_address,
            duration: null,
          },
          receiverContact: {
            name: data.contact,
            phone: data.mobile,
            email: data.email,
          },
          notes: data.notes,
        };
        resp = yield call(
          api.create,
          {
            ...request,
          },
          headers,
        );
        if (!resp.success) {
          throw new Error(
            resp.messages && resp.messages[0]
              ? resp.messages[0]
              : I18n.str(
                'createNewSalesOrderFailed',
                'Unable to create sales order, please try again later.',
              ),
          );
        }
      } else {
        request = {
          ...request,
          dropoff: {
            address: data.waitingAddress[0].formatted_address,
            arrivalWindow: {
              begin: moment(data.deliveryDate)
                .add(3, 'hours')

                .toISOString(),
              end: moment(data.deliveryDate)
                .add(3, 'hours')
                .toISOString(),
              excludeBegin: true,
              excludeEnd: true,
            },
            coordinate: getCoordinates(data.waitingAddress[0]),
            description: data.waitingAddress[0].formatted_address,
            duration: null,
          },
          categoryId: data.categoryId,
        };
        resp = yield call(
          api.createForecast,
          {
            ...request,
          },
          headers,
        );
        if (!resp.success) {
          throw new Error(
            resp.messages && resp.messages[0]
              ? resp.messages[0]
              : I18n.str(
                'createNewSalesOrderFailed',
                'Unable to create sales order, please try again later.',
              ),
          );
        }
      }
    } else {
      request = {
        ...request,
        salesOrderNumber: data.salesOrderNumber,
        salesOrderLineNumber: parseFloat(data.salesOrderLineNumber),
        itemNumber: data.itemNumber,
        dropoff: {
          address: data.warehouseLoadingPoint.name,
          arrivalWindow: {
            begin: moment(data.deliveryDate)
              .add(3, 'hours')
              .toISOString(),
            end: moment(data.deliveryDate)
              .add(3, 'hours')
              .toISOString(),
            excludeBegin: true,
            excludeEnd: true,
          },
          coordinate: {
            latitude: data.warehouseLoadingPoint.location.lat,
            longitude: data.warehouseLoadingPoint.location.lng,
          },
          description: data.warehouseLoadingPoint.name,
          duration: null,
        },
        notificationContact: {
          name: data.contact,
          phone: data.mobile,
          email: data.email,
        },
        notes: data.notes,
      };
      resp = yield call(
        api.createExWork,
        {
          ...request,
        },
        headers,
      );
      if (!resp.success) {
        throw new Error(
          resp.messages && resp.messages[0]
            ? resp.messages[0]
            : I18n.str(
              'createNewSalesOrderFailed',
              'Unable to create sales order, please try again later.',
            ),
        );
      }
    }

    yield put(
      showNotification(
        I18n.str('createNewSalesOrderSuccessfully', 'The sales order is created successfully!'),
        'success',
      ),
    );
    yield put(created());
    if (resp && resp.response && resp.response[0] && resp.response[0].orderMasterId) {
      yield context.history.push(
        generatePath(ORDER_ROUTES.ROOT, { oId: resp.response[0].orderMasterId }),
      );
      if (resp.response[0].waybillUrl) yield window.open(resp.response[0].waybillUrl);
    } else {
      yield context.history.push(generatePath(ROUTES.ROOT));
    }
  } catch (error) {
    yield put(failed());

    if (resp && resp.response && resp.response[0] && resp.response[0].orderMasterId) {
      yield context.history.push(
        generatePath(ORDER_ROUTES.ROOT, { oId: resp.response[0].orderMasterId }),
      );
      if (resp.response[0].waybillUrl) yield window.open(resp.response[0].waybillUrl);
    } else if (
      resp
      && resp.messages
      && resp.messages
      && resp.messages.length > 0
      && data.modeOfDelivery === 'DAT'
    ) {
      yield put(showNotification(resp.messages[0], 'error'));
    } else {
      yield put(
        showNotification(
          I18n.str(
            'createNewSalesOrderFailed',
            'Unable to create sales order, please try again later.',
          ),
          'error',
        ),
      );
    }

    yield call(exception, error);
  }
}

function* doFetchFleets({ payload }) {
  const { fleetName } = payload;

  const state = yield select();

  const headers = yield call(getCurrentAuthHeader, state);

  let availableFleets = [
    {
      label: 'Any',
      value: {
        name: 'Any',
        account: null,
      },
    },
  ];

  try {
    let hasNext = true;
    let cursor = null;

    while (hasNext === true) {
      const resp = yield call(
        api.fetchFleets,
        {
          name: fleetName,
          type: 'COURIER',
          cursor,
        },
        headers,
      );

      const currentFleets = resp.results.map((fleet) => ({
        label: fleet.value.name,
        value: {
          name: fleet.value.name,
          account: fleet.key,
        },
      }));

      availableFleets = availableFleets.concat(currentFleets);

      hasNext = resp.nextPage.hasResults;
      cursor = resp.nextPage.cursor;
    }

    yield put(fetchedFleets(availableFleets));
  } catch (e) {
    yield put(fetchedFleets(availableFleets));
    yield put(
      showNotification(I18n.str('fetchingFleetsFailed', 'Unable to fetch fleets.'), 'error'),
    );
  }
}

function* watcher(context) {
  yield all([takeEvery(create().type, doCreate, context)]);
  yield takeLatest(fetchFleets().type, doFetchFleets);
}

export default watcher;
